import type { ReactNode } from 'react';

import { NOTHING } from '@/lib/nothing';

import styles from './CandidateFacts.module.css';

/**
 * A panel's worth of labelled facts.
 *
 * A description list rather than a grid of divs, because that is what this is: each
 * row is a term and its value, and a screen reader reads them as pairs instead of as
 * a run of loose text.
 */
export function Facts({ children }: { children: ReactNode }) {
  return <dl className={styles.facts}>{children}</dl>;
}

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.fact}>
      <dt className={styles.term}>{label}</dt>
      <dd className={styles.value}>{children}</dd>
    </div>
  );
}

/** Free text, or a faint dash. Never an empty row; absence is information too. */
export function FactText({ value }: { value: string | null | undefined }) {
  return value ? <>{value}</> : <span className={styles.none}>{NOTHING}</span>;
}

/**
 * Who is handling the record, and whether they still can.
 *
 * A candidate keeps its assignee when that staff member is removed, on purpose:
 * restoring somebody brings them back with their history intact, so clearing the
 * column on the way out would hand back an account owning nothing. The cost is that
 * a departed colleague reads exactly like a present one, and a record nobody is
 * actually working sits there looking handled. The name stays and says so.
 */
export function FactAssignee({
  assignee,
}: {
  assignee: { full_name: string; is_active: boolean } | null | undefined;
}) {
  if (!assignee) return <span className={styles.none}>{NOTHING}</span>;
  return (
    <>
      {assignee.full_name}
      {assignee.is_active ? null : <span className={styles.departed}> no longer active</span>}
    </>
  );
}
